import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Upper Footer — white background section                             */
/* ------------------------------------------------------------------ */
export const UpperFooter = styled.footer`
  width: 100%;
  background-color: ${colors.primitives.neutral0};
  position: relative;
  z-index: 1;
  border-radius: 0 0 ${sizes.spacing[24]}px ${sizes.spacing[24]}px;
`

/* EGDE-487 r2: the 1920 Figma footer keeps only ~144px side gaps (content
   ~1632px wide), while the shared Container pads 360px there — QA flagged the
   resulting narrow box. Same geometry as Container at lg/xl. */
export const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: ${breakpoints.lg}px;
    padding: 0 ${layoutPadding.desktop}px;
    margin: 0 auto;
  }

  @media (min-width: ${breakpoints.xl}px) {
    max-width: ${breakpoints.xl}px;
    padding: 0 ${layoutPadding.desktopL}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    max-width: ${breakpoints.xxl}px;
    padding: 0 144px;
  }
`

export const UpperFooterInner = styled.div`
  padding: ${sizes.spacing[48]}px ${sizes.spacing[16]}px;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[32]}px;
  /* EGDE-507: the design separates the footer from the page content with a
     thin divider (neutral200 reads invisible on white, cf. EGDE-345). */
  border-top: 1px solid ${colors.primitives.neutral300};

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
    gap: ${sizes.spacing[48]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Logo — links to homepage                                            */
/* ------------------------------------------------------------------ */
export const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`

export const LogoImageWrapper = styled.div`
  position: relative;
  height: 48px;
  width: 138px;

  @media (min-width: ${breakpoints.lg}px) {
    height: 65px;
    width: 188px;
  }
`

/* EGDE-487: groups the logo with the nav columns. Below 1920 it stacks them
   (logo on its own row above, as before); at 1920 the Figma footer places the
   logo INLINE at the columns' inline-start edge (visually right in RTL), so
   the row direction flips and the columns fill the remaining width. */
export const NavRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[32]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    flex-direction: row;
    align-items: flex-start;
  }
`

/* ------------------------------------------------------------------ */
/* Navigation columns — desktop horizontal, mobile accordion           */
/* ------------------------------------------------------------------ */
export const NavColumns = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    flex-direction: row;
    gap: ${sizes.spacing[48]}px;
    align-items: flex-start;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    flex: 1;
  }
`

export const NavColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
`

export const NavColumnTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

export const NavLink = styled(Link)`
  text-decoration: none;
  color: ${colors.light.text.primary};
  transition: color 0.2s;

  &:hover {
    color: ${colors.primitives.primary500};
  }

  /* EGDE-507: top-level page links wrap NavColumnTitle, whose own color
     swallowed the hover — inherit so the whole link responds like the
     sub-links do. */
  ${NavColumnTitle} {
    color: inherit;
  }
`

export const NavLinkText = styled(Typography)`
  color: inherit;
`

/* ------------------------------------------------------------------ */
/* Additional sites (CTA links with arrows)                            */
/* ------------------------------------------------------------------ */
export const AdditionalSitesLabel = styled(Typography)`
  color: ${colors.light.text.primary};
`

/* EGDE-159: Mobile additional sites links — no accordion animation.
   EGDE-507: QA "still too tight" — the mobile design spaces the label and
   each subsidiary link ~36px centre-to-centre, i.e. 16px gaps. */
export const AdditionalSitesMobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding-top: ${sizes.spacing[16]}px;
`

/* EGDE-44: Arrow must flip direction + position in LTR (English).
   align-self/width keep each link hugging its content so the column
   sits flush under the AdditionalSitesLabel title instead of stretching. */
const ctaRowStyles = css`
  display: inline-flex;
  align-self: flex-start;
  width: fit-content;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  text-decoration: none;
  color: ${colors.light.text.primary};
  transition: color 0.2s;

  svg {
    color: ${colors.primitives.primary500};
    flex-shrink: 0;
    /* EGDE-507: hover nudges the arrow along its pointing direction via
       logical margins (translateX is direction-flipped by stylis-rtl). */
    transition:
      color 0.2s,
      margin 0.2s ease;
  }

  /* EGDE-507: desktop rest state is black — the arrow only turns orange
     (and shifts) on hover. Mobile keeps the always-orange design arrow. */
  @media (min-width: ${breakpoints.lg}px) {
    svg {
      color: ${colors.light.text.primary};
    }
  }

  &:hover {
    color: ${colors.primitives.primary500};

    svg {
      color: ${colors.primitives.primary500};
      margin-inline-start: ${sizes.spacing[4]}px;
      margin-inline-end: -${sizes.spacing[4]}px;
    }
  }

  [dir="ltr"] & {
    flex-direction: row-reverse;

    svg {
      /* @noflip */
      transform: scaleX(-1);
    }
  }
`

export const CtaLinkRow = styled.a`
  ${ctaRowStyles}
`

export const CtaInternalLinkRow = styled(Link)`
  ${ctaRowStyles}
`

export const CtaLinkText = styled(Typography)`
  color: inherit;
  font-weight: 700 !important;
`

/* ------------------------------------------------------------------ */
/* Mobile accordion                                                    */
/* ------------------------------------------------------------------ */
export const MobileAccordion = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* EGDE-40: No divider lines between accordion categories on mobile */
export const AccordionItem = styled.div``

export const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${sizes.spacing[16]}px 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.light.text.primary};
`

export const AccordionLinkRow = styled(Link)`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${sizes.spacing[16]}px 0;
  text-decoration: none;
  color: ${colors.light.text.primary};
  cursor: pointer;

  &:hover {
    color: ${colors.primitives.primary500};
  }
`

export const AccordionTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700;
`

/* EGDE-39: Plus/Minus icon should be orange */
export const AccordionIconWrapper = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primitives.primary500};
`

export const AccordionContent = styled.div<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  overflow: hidden;
  max-height: ${(p) => (p.$expanded ? "500px" : "0")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  padding-bottom: ${(p) => (p.$expanded ? `${sizes.spacing[16]}px` : "0")};
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease,
    padding-bottom 0.3s ease;
`

/* ------------------------------------------------------------------ */
/* Bottom bar — legal links, social icons, email                       */
/* ------------------------------------------------------------------ */
/* EGDE-42: On mobile, reorder so email appears below social links */
export const BottomBar = styled.div`
  display: flex;
  flex-direction: column;
  /* EGDE-507: mobile design separates legal links / social icons / email by
     ~32px (QA round 2: 16px read "still too tight"). Desktop keeps its row. */
  gap: ${sizes.spacing[32]}px;
  padding-top: ${sizes.spacing[32]}px;
  border-top: 1px solid ${colors.primitives.neutral200};

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${sizes.spacing[24]}px;
  }
`

export const LegalLinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  order: 1;
  /* EGDE-43: keep the pipe-divider model (per Figma) on all viewports.
     Horizontal spacing comes from each item's padding/divider (column-gap 0);
     wrapped mobile rows get a 16px vertical gap. */
  column-gap: 0;
  row-gap: ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    order: unset;
    row-gap: 0;
  }
`

/* EGDE-43: spacing + the pipe divider live ONLY on the inline-END of each link
   (Figma keeps pipe dividers between legal links). Putting the gap on the
   trailing edge — never the leading edge — means the FIRST item of every line,
   including a wrapped second line, hugs the same inline-start edge with zero
   offset. That is exactly the "second line must align to the first" QA fix:
   with a leading padding instead, the wrapped line's first item was pushed 12px
   off the start edge. The divider also stays at the END of each line's last
   visible item (the left end of the upper line in RTL), matching Figma. */
export const LegalLink = styled(Link)`
  text-decoration: none;
  color: ${colors.light.text.primary};
  line-height: 1;
  transition: color 0.2s;
  padding-inline-end: ${sizes.spacing[12]}px;
  margin-inline-end: ${sizes.spacing[12]}px;
  border-inline-end: 1px solid ${colors.primitives.neutral300};

  /* The LAST legal link carries no trailing pipe / spacing. ":not(:has(~ &))" =
     "has no following LegalLink sibling", which correctly targets the last
     legal link even when the (mobile-hidden) in-row email <a> follows it in the
     DOM — unlike :last-of-type, which the email would steal. */
  &:not(:has(~ &)) {
    border-inline-end: none;
    padding-inline-end: 0;
    margin-inline-end: 0;
  }

  &:hover {
    color: ${colors.primitives.primary500};
  }
`

export const LegalLinkText = styled(Typography)`
  color: inherit;
`

/* EGDE-30: Desktop in-row email — LAST item in the legal-links row, flush
   after the privacy-policy link with the same inline-start divider, so it
   sits adjacent to privacy at the inline-end (matches Figma). Hidden on
   mobile (the mobile email is rendered as EmailLinkMobile below the social
   row, preserving the existing mobile arrangement). */
export const EmailLink = styled.a`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: inline-flex;
    align-items: center;
    text-decoration: underline;
    color: ${colors.light.text.primary};
    transition: color 0.2s;
    padding-inline: ${sizes.spacing[12]}px;
    /* EGDE-295: the preceding privacy-policy link drops its trailing spacing, so
       without a leading margin the email's divider pipe sat flush against the
       privacy-policy text. Add 12px before the pipe so it is spaced symmetrically
       like every other legal-link divider (logical → correct in LTR and RTL). */
    margin-inline-start: ${sizes.spacing[12]}px;
    border-inline-start: 1px solid ${colors.primitives.neutral300};
    line-height: 1;
  }

  &:hover {
    color: ${colors.primitives.primary500};
  }
`

/* EGDE-30/42: Mobile-only email — sits below the social row (order 3),
   underlined and aligned to the inline-start. Hidden on desktop. */
export const EmailLinkMobile = styled.a`
  text-decoration: underline;
  color: ${colors.light.text.primary};
  transition: color 0.2s;
  order: 3;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }

  &:hover {
    color: ${colors.primitives.primary500};
  }
`

export const EmailText = styled(Typography)`
  color: inherit;
`

export const SocialLinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[16]}px;
  order: 2;

  @media (min-width: ${breakpoints.lg}px) {
    gap: 20px;
    order: unset;
    margin-inline-start: auto;
  }
`

export const SocialIconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 26px;
    height: 26px;

    @media (min-width: ${breakpoints.lg}px) {
      width: 30px;
      height: 30px;
    }
  }
`

/* ------------------------------------------------------------------ */
/* Bottom Footer — background image with overlay                       */
/* ------------------------------------------------------------------ */
export const BottomFooter = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  height: 375px;
  overflow: hidden;
  margin-top: -80px;
  /* EGDE-318 footer reveal: the band's box is a WINDOW onto the fixed layer
     inside — clip-path (unlike overflow) also clips fixed-position
     descendants, so the image stays pinned to the viewport bottom and is
     progressively revealed as the white page sheet scrolls up off it
     (the prototype's ending). */
  clip-path: inset(0);

  @media (min-width: ${breakpoints.lg}px) {
    height: 680px;
    margin-top: -208px;
  }
`

/* EGDE-318: viewport-bottom-pinned layer holding the band's bg + content.
   Same height as the band, so at full scroll the crop matches the static
   design exactly; mid-scroll the band's top edge "uncovers" it. */
export const BottomFooterFixed = styled.div`
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  height: 375px;

  @media (min-width: ${breakpoints.lg}px) {
    height: 680px;
  }
`

export const BottomFooterBg = styled.div`
  position: absolute;
  inset: 0;
`

// EGDE-266: the footer background is authored separately for desktop and mobile
// in the CMS (backgroundImageDesktop / backgroundImageMobile). Toggle the two
// wrappers purely via CSS media queries so the correct asset shows per viewport
// without any client-side viewport detection (SSR-safe, no hydration flash).
export const BottomFooterBgDesktop = styled(BottomFooterBg, {
  shouldForwardProp: (prop) => prop !== "$allViewports",
})<{ $allViewports?: boolean }>`
  display: ${(p) => (p.$allViewports ? "block" : "none")};

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const BottomFooterBgMobile = styled(BottomFooterBg)`
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const BottomFooterOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
`

export const BottomFooterContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding: ${sizes.spacing[72]}px ${sizes.spacing[16]}px ${sizes.spacing[48]}px;
  gap: ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[120]}px;
    justify-content: flex-end;
  }
`

export const BottomLogoImage = styled.img`
  width: 64px;
  height: 64px;

  @media (min-width: ${breakpoints.lg}px) {
    width: 80px;
    height: 80px;
  }
`

export const CopyrightText = styled(Typography)`
  color: ${colors.primitives.neutral0};
  text-align: center;
`

export const CreatedByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.primitives.neutral0};
`

export const CreatedByText = styled(Typography)`
  color: ${colors.primitives.neutral0};
`

/* ------------------------------------------------------------------ */
/* Footer wrapper (root)                                               */
/* ------------------------------------------------------------------ */
export const FooterRoot = styled.div`
  width: 100%;
`
